import { useEffect, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { Button, Icon, Layout, Page, Tag, Text } from '@loadsmart/miranda-react'
import { getToken } from '@loadsmart/miranda-tokens'

import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import ReportComponent from 'managed-analytics/ui/components/demoReport/Report'
import { getPBIDemoReportQueryOptions } from 'managed-analytics/use-cases/getPBIReports'

const styles = css`
  display: none;

  @media screen and (min-width: ${getToken('media-screen-desktop')}) {
    display: block;
  }
`

const StyledBackButton = styled(Button)`
  ${styles}
`

const StyledTag = styled(Tag)`
  ${styles}
`

export default function ReportPage() {
  const { reportId } = useParams()

  const navigate = useNavigate()

  const [isZendeskDialogOpen, setIsZendeskDialogOpen] = useState(false)

  const demoReportQuery = useSuspenseQuery(getPBIDemoReportQueryOptions(reportId!))

  const { track } = useAnalytics()

  useEffect(() => {
    track(AnalyticsEvent.OpenReport, {
      'Report Name': demoReportQuery.data.reportName,
      'Dataset Id': demoReportQuery.data.datasetId,
      'Dataset Group Id': demoReportQuery.data.groupId,
      Demo: true,
    })
  }, [track, demoReportQuery.data])

  const isUnavailable = demoReportQuery.data.demoWithoutData

  return (
    <AppLayout>
      <Header
        unauthCtaLabel="Sign in"
        unauthCtaHref="/"
        title={
          <Layout.Group align="center" gap="spacing-2">
            <StyledBackButton
              variant="icon"
              leading={<Icon name="send" />}
              size="small"
              href="/demo"
              onClick={e => {
                e.preventDefault()

                navigate('/demo')
              }}
            >
              <Icon name="arrow-left" size="12px" />
            </StyledBackButton>
            <Text color="color-text-primary" variant="body-md-bold">
              {demoReportQuery.data.reportName}
            </Text>
            <StyledTag>Preview</StyledTag>
          </Layout.Group>
        }
        actions={
          <Button
            variant="primary"
            leading={<Icon name="send" />}
            size="small"
            onClick={() => setIsZendeskDialogOpen(true)}
          >
            Request report
          </Button>
        }
      />
      <Page.Content>
        <Page.Body>
          <div
            style={{
              opacity: isUnavailable ? 0.4 : 1,
              pointerEvents: isUnavailable ? 'none' : 'auto',
              display: 'flex',
              height: '100%',
              width: '100%',
              flex: 1,
            }}
          >
            <ReportComponent
              groupId={demoReportQuery.data.groupId}
              reportId={demoReportQuery.data.reportId}
              reportName={demoReportQuery.data.reportName}
              accessToken={demoReportQuery.data.embedToken ?? ''}
              isReportLoading={demoReportQuery.isLoading}
            />
          </div>
        </Page.Body>
      </Page.Content>

      <ZendeskDialog
        requestType={RequestType.NewRequest}
        open={isZendeskDialogOpen}
        onClose={() => setIsZendeskDialogOpen(false)}
      />
    </AppLayout>
  )
}
